import React, { Component } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'

import Button from '../../UniformComponents/Button'
import Modal from '../../UniformComponents/Modal'
import FormController from '../form/FormController'
import InfoAccordion from '../InfoAccordion'
import RecordTag from '../RecordTags'
import SearchIcon from '../../assets/images/portfolio_search_icon.svg'
import FolderIcon from '../../assets/images/portfolio_openfolder.svg'

@observer
class PortfolioEdit extends Component {
    constructor(props) {
        super(props)
        this.fields = {}
        this.state = {
            isProductsAccordionOpen: false,
            isGuidanceAccordionOpen: true,
        }
    }

    areRecordTagsValid = () => this.fields.recordTag.validate()

    areFieldsValid = () => {
        const { portfolioStore } = this.props
        const fields = portfolioStore.isMultiDivision
            ? ['name', 'industry', 'division']
            : ['name', 'industry']

        return fields
            .map((field) => (this.fields[field]({
                value: portfolioStore[field],
                name: field,
            }, true)))
            .every((valid) => valid)
    }

    register = (name, validate, path) => {
        this.fields[name] = {
            validate,
            path,
        }
    }

    unregister = (name) => {
        Reflect.deleteProperty(this.fields, name)
    }

    save = async () => {
        const {
            store, portfolioStore, portfolioId, handleCloseModal,
        } = this.props

        const fieldsValid = this.areFieldsValid()
        const recordTagsValid = this.areRecordTagsValid()
        if (!fieldsValid || !recordTagsValid) return

        const obj = {
            portfolioId: parseInt(portfolioId, 10),
            name: portfolioStore.name,
            industry: portfolioStore.industry,
            divisionId: portfolioStore.division,
        }
        const isEditing = portfolioId !== 0

        try {
            if (isEditing) {
                await portfolioStore.update(obj)
                await portfolioStore.saveTags(portfolioId)
            } else {
                const portfolio = await portfolioStore.save(obj)
                await portfolioStore.saveTags(portfolio.portfolioId)
            }

            handleCloseModal()
            await portfolioStore.initSearch()
            store.addNotification(null, `${isEditing ? i18n.t('update_portfolio') : i18n.t('create_portfolio')}`, 'success')
        } catch (e) {
            console.log(e)
            store.addNotification(null, `Error ${isEditing ? 'updating' : 'creating'} portfolio`, 'error')
        }
    }

    renderButtons = () => {
        const { handleCloseModal } = this.props
        return (
            <div className="form-button-wrapper">
                <Button
                    label={i18n.t('close')}
                    icon=""
                    cssClass="regular tertiary"
                    onClick={handleCloseModal}
                />
                <Button
                    label={i18n.t('save')}
                    icon="fa-light fa-floppy-disk"
                    cssClass="regular primary"
                    onClick={this.save}
                />
            </div>
        )
    }

    renderProductsAccordion = () => {
        const { portfolioStore, portfolioId } = this.props
        const { isProductsAccordionOpen } = this.state
        if (portfolioId === 0 || !portfolioStore.products?.length) return null
        return (
            <InfoAccordion
                title={i18n.t('portfolioEdit.accordion.products')}
                show={isProductsAccordionOpen}
                toggle={this.toggleProductAccordion}
            >
                {portfolioStore.products.map((product) => (
                    <p>{product.name}</p>
                ))}
            </InfoAccordion>
        )
    }

    toggleProductAccordion = () => {
        this.setState((prevState) => ({
            isProductsAccordionOpen: !prevState.isProductsAccordionOpen,
        }))
    }

    renderGuidanceAccordion = () => {
        const { isGuidanceAccordionOpen } = this.state
        return (
            <InfoAccordion
                title={i18n.t('portfolioEdit.accordion.guidance.title')}
                show={isGuidanceAccordionOpen}
                toggle={this.toggleGuidanceAccordion}
                cssClass="mb-35"
                infoContentClass="p-8"
            >
                <div className="card-wrapper bg-white">
                    <div className="card">
                        <img src={SearchIcon} alt="magnifying glass icon" />
                        <div>
                            <p className="subtitle">{i18n.t('portfolioDivisionEdit.accordion.guiance.step.1')}</p>
                            <div dangerouslySetInnerHTML={{
                                __html: i18n.t('portfolioEdit.accordion.guidance.desc.1.1'),
                            }}
                            />
                            <div dangerouslySetInnerHTML={{
                                __html: i18n.t('portfolioEdit.accordion.guidance.desc.1.2'),
                            }}
                            />
                        </div>
                    </div>
                    <div className="card">
                        <img src={FolderIcon} alt="open folder icon" />
                        <div>
                            <p className="subtitle">{i18n.t('portfolioDivisionEdit.accordion.guiance.step.2')}</p>
                            <div dangerouslySetInnerHTML={{
                                __html: i18n.t('portfolioDivisionEdit.accordion.guidance.desc.2.1'),
                            }}
                            />
                            <div dangerouslySetInnerHTML={{
                                __html: i18n.t('portfolioEdit.accordion.guidance.desc.2.2'),
                            }}
                            />
                        </div>
                    </div>
                </div>
            </InfoAccordion>
        )
    }

    toggleGuidanceAccordion = () => {
        this.setState((prevState) => ({
            isGuidanceAccordionOpen: !prevState.isGuidanceAccordionOpen,
        }))
    }

    render() {
        const {
            showModal, handleCloseModal, portfolioStore, portfolioId,
        } = this.props
        return (
            <Modal show={showModal} onClose={handleCloseModal} loading={portfolioStore.formLoading}>
                <div className="form-header">
                    <h2>{portfolioId !== 0 ? i18n.t('edit_portfolio') : i18n.t('add_portfolio')}</h2>
                    <p className="desc">
                        {portfolioId !== 0 ? i18n.t('edit_portfolio_desc') : i18n.t('add_portfolio_desc')}
                    </p>
                </div>
                {this.renderGuidanceAccordion()}
                <FormController
                    fields={this.fields}
                    formFields={portfolioStore.formFields}
                    setField={portfolioStore.setField}
                />
                <RecordTag
                    values={portfolioStore.recordTags}
                    availableTags={portfolioStore.availableTags}
                    onBlur={portfolioStore.setTag}
                    register={this.register}
                    unregister={this.unregister}
                />
                <br />
                {this.renderProductsAccordion()}
                {this.renderButtons()}
            </Modal>
        )
    }
}

export default PortfolioEdit
